import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIsMobileV2 } from 'src/application/hooks/useIsMobile';
import { submitQuizResults } from 'src/application/store/reducers/student/tracking/trackedQuizActions';
import { FroalaEditorsSettings } from 'src/config/configs';

import { closeQuiz, loadNextQuestion } from '../../../actions/contentItemActions';
import { AudioCaptionsContextProvider } from '../../../components/Audio/AudioCaptionsContextProvider';
import { FullScreenContentModal } from '../../../components/modal/FullScreenContentModalNavigation/FullScreenContentModal';
import { Fade } from '../../../components/transitions/Fade';
import { getMainProjectPanorama } from '../../../globalPanoramaAccessor';
import { useOnMount } from '../../../hooks/useOnMount';
import { useStudentDispatch } from '../../../hooks/useStudentDispatch';
import { Guid } from '../../../store/model/guid';
import { getItemByIdSelector, getItemByIdTypedSelector } from '../../../store/reducers/items/itemsSelectors';
import { addCompletedItem } from '../../../store/reducers/student/tracking/itemsTrackingActions';
import { useStudentSelector } from '../../../store/selectors/useStudentSelector';
import { AssertionException } from '../../../utils/log';
import { MemoizedContentRequirementsProvider } from '../../RestrictNavigation/ContentRequirementsProvider';
import { BlindMap } from './BlindMap';
import { Result } from './Results/Result';
import { isBlindMapQuestion, isQuestion, Quiz, isTestContentItem } from '@ael/core-api';
import { TransitionGroup } from 'react-transition-group';

export type ResultType = Record<string, boolean>;

const defaultSuccessRate = 80;

interface TestMeProps {
  currentItem: Quiz;
  froalaEditorsSettings: FroalaEditorsSettings;
}

export const TestMe: React.FC<TestMeProps> = ({ currentItem, froalaEditorsSettings }) => {
  // Container logic (formerly in TestMe container)
  const blindMapIntroductionDismissed = useStudentSelector(
    (state) => state.introductionsDismissed.blindMapIntroductionDismissed
  );
  const currentQuiz = useStudentSelector((state) => state.currentQuiz);
  const blindMapGuessedIncorrectly = useStudentSelector((state) => state.blindMap.guessedIncorrectly);
  const dispatch = useStudentDispatch();
  const getItemByIdTyped = useStudentSelector(getItemByIdTypedSelector);
  const getItemById = useStudentSelector(getItemByIdSelector);
  const isMobile = useIsMobileV2();

  const currentContentItem = currentQuiz.currentContentItem
    ? getItemByIdTyped(currentQuiz.currentContentItem, isTestContentItem)
    : null;

  // Derived state
  const quiz = currentItem;
  let nextQuestion: string | null = null;
  let shouldDisplayBlindMapIntroduction = false;
  const displayBlindMapHintButton = blindMapGuessedIncorrectly;

  if (currentContentItem) {
    const currentContentItemIndex = currentItem.contentItems.indexOf(currentContentItem.id);
    const isFirstQuestion = currentContentItemIndex === 0;
    const isLastQuestion = currentContentItemIndex === currentItem.contentItems.size - 1;
    const previousItemId = currentItem.contentItems.get(currentContentItemIndex - 1);
    shouldDisplayBlindMapIntroduction =
      !blindMapIntroductionDismissed &&
      isBlindMapQuestion(currentContentItem) &&
      (isFirstQuestion || (previousItemId !== undefined && !isBlindMapQuestion(getItemById(previousItemId))));

    nextQuestion = isLastQuestion ? null : currentItem.contentItems.get(currentContentItemIndex + 1) ?? null;
  }

  // Action handlers
  const handleClose = useCallback(
    (isSuccessful: boolean) => {
      if (isSuccessful) {
        dispatch(addCompletedItem(quiz.id));
      }
      dispatch(closeQuiz(quiz.id, isMobile));
    },
    [dispatch, isMobile, quiz.id]
  );

  const handleSubmit = useCallback(
    (currentQuestionId: Guid | undefined, nextQuestionId: string | null) => {
      return () => {
        if (currentQuestionId) {
          dispatch(addCompletedItem(currentQuestionId));
        }
        dispatch(loadNextQuestion(nextQuestionId));
      };
    },
    [dispatch]
  );

  // Component logic (formerly in TestMe component)
  const [results, setResults] = useState<ResultType>({});
  const ref = useRef(null);
  const panorama = getMainProjectPanorama();
  const successRate = quiz.isMandatory ? quiz.successRate || defaultSuccessRate : null;
  const startingPosition = quiz.startingPosition;

  useEffect(() => {
    if (currentQuiz.displayResults) {
      const totalQuestions = Object.keys(results).length;
      const correctAnswers = Object.values(results).filter((correct) => correct).length;
      dispatch(submitQuizResults(currentItem.id, (correctAnswers / totalQuestions) * 100, successRate));
    }
  }, [currentItem.id, currentQuiz.displayResults, dispatch, results, successRate]);

  const submitAnswer = useCallback((questionId: string, correct: boolean) => {
    setResults((prev) => {
      if (prev[questionId] !== undefined) {
        // If question with ID was already answered, do not change it
        return prev;
      }
      const newResults = { ...prev };
      newResults[questionId] = correct;
      return newResults;
    });
  }, []);

  useOnMount(() => {
    if (startingPosition) {
      panorama.lookToCameraPosition(startingPosition);
    }
  });

  // Render logic
  if (currentQuiz.displayResults && results) {
    return (
      <Result
        totalQuestions={Object.keys(results).length}
        correctAnswers={Object.values(results).filter((correct) => correct).length}
        successRate={successRate}
        onClose={handleClose}
      />
    );
  }

  if (!currentContentItem) {
    return null;
  }

  if (isBlindMapQuestion(currentContentItem)) {
    return (
      <BlindMap
        key={currentContentItem.id}
        blindMapItem={currentContentItem}
        shouldDisplayBlindMapIntroduction={shouldDisplayBlindMapIntroduction}
        displayBlindMapHintButton={displayBlindMapHintButton}
        onSubmit={(questionId, correct) => {
          submitAnswer(questionId, correct);
          if (correct) {
            handleSubmit(currentContentItem.id, nextQuestion)();
          }
        }}
      />
    );
  }

  if (isQuestion(currentContentItem)) {
    return (
      <>
        <TransitionGroup>
          <Fade ref={ref} key="question">
            <AudioCaptionsContextProvider>
              <MemoizedContentRequirementsProvider item={currentContentItem} onWidgetSubmit={submitAnswer}>
                <FullScreenContentModal
                  ref={ref}
                  contentItem={currentContentItem}
                  onContinue={handleSubmit(currentContentItem.id, nextQuestion)}
                  froalaEditorsSettings={froalaEditorsSettings}
                />
              </MemoizedContentRequirementsProvider>
            </AudioCaptionsContextProvider>
          </Fade>
        </TransitionGroup>
      </>
    );
  }

  throw AssertionException('Invalid quiz content item type.');
};
